import { useAuth0 } from '@auth0/auth0-react';
import { RootState } from '@store/reducers';
import React, { ComponentType, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { useHistory } from 'react-router-dom';
import { useIsInIframe } from 'shared-components';
import { useApplyWizardContext } from '../context/ApplyWizardContext';
import {
  ApplyWizardSteps,
  STEP_INTEGRATED_LOGIN,
  STEP_HOMEPAGE,
  STEP_BCM_HOMEPAGE,
  STEP_BCM_2_HOMEPAGE,
} from '@components/ApplyWizard/ApplyWizardSteps';

const appSelector = formValueSelector('application');

interface EnsureLoggedInOptions {
  requireBusinessId?: boolean;
}

export const ensureLoggedIn = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options?: EnsureLoggedInOptions
) => {
  const requireBusinessId = options?.requireBusinessId ?? false;

  const mapStateToProps = (state: RootState) => {
    return {
      user: state.user,
      ...(requireBusinessId ? { businessId: appSelector(state, 'business_id') } : {}),
    };
  };

  const connector = connect(mapStateToProps);
  type PropsFromRedux = ConnectedProps<typeof connector>;
  interface EnsureLoggedInProps extends PropsFromRedux {
    WrappedComponent: ComponentType<any>;
    businessId?: string;
  }

  const EnsureLoggedIn: React.FC<EnsureLoggedInProps> = (props) => {
    const { user, businessId, ...rest } = props;
    const auth0 = useAuth0();
    const history = useHistory();
    const isOpenedInIframe = useIsInIframe();
    const { changeStepTo } = useApplyWizardContext();

    useEffect(() => {
      // Skip redirect while Auth0 SDK is loading or while Auth0 says authenticated
      // but Redux user.loggedIn hasn't been set yet (Auth0LoggedInSetState is async).
      if (auth0.isLoading || auth0.isAuthenticated) return;

      if (!user.loggedIn) {
        if (changeStepTo && isOpenedInIframe) {
          changeStepTo(ApplyWizardSteps[STEP_INTEGRATED_LOGIN].id);
        } else {
          auth0.loginWithRedirect({
            authorizationParams: {
              connection: 'email',
            },
            appState: {
              returnTo: window.location.pathname,
            },
          });
        }
      }
    }, [user, auth0]);

    // Logged in but no business_id — user skipped earlier steps.
    // Redirect to the flow's home page to prevent 400s on save.
    useEffect(() => {
      if (requireBusinessId && user.loggedIn && !businessId) {
        const path = window.location.pathname;
        if (changeStepTo) {
          const homeStep = path.startsWith('/bcm2')
            ? STEP_BCM_HOMEPAGE
            : path.startsWith('/bcm')
            ? STEP_BCM_2_HOMEPAGE
            : STEP_HOMEPAGE;
          changeStepTo(homeStep);
        } else {
          const home = path.startsWith('/bcm2') ? '/bcm2' : path.startsWith('/bcm') ? '/bcm' : '/';
          history.replace(home);
        }
      }
    }, [user.loggedIn, businessId]);

    if (!user.loggedIn) return null;
    if (requireBusinessId && !businessId) return null;

    return <WrappedComponent {...(rest as P)} />;
  };

  return connector(EnsureLoggedIn);
};

export default ensureLoggedIn;
